import { Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuditLogService, AuditLog, AuditLogFilters } from '../../../services/audit-log';
import { CounselorNav } from '../../views/counselor-view/counselor-nav/counselor-nav';

@Component({
  selector: 'app-audit-logs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatTableModule,
    MatProgressSpinnerModule,
    CounselorNav,
  ],
  templateUrl: './audit-logs.html',
  styleUrl: './audit-logs.css',
})
export class AuditLogs implements OnInit {
  private auditLogService = inject(AuditLogService);
  private cdr = inject(ChangeDetectorRef);

  logs: AuditLog[] = [];
  total = 0;
  loading = false;
  error = '';

  displayedColumns = [
    'timestamp',
    'eventType',
    'action',
    'performedBy',
    'studentId',
    'reason',
    'disclosedTo',
    'consent',
    'status',
  ];

  /** Base filter state. `offset` is mutated directly by nextPage/prevPage. */
  filters: AuditLogFilters = { limit: 50, offset: 0 };

  /** Separate filter fields bound to the UI controls, merged into `filters` on load. */
  searchEventType = '';
  filterSuccess = '';
  fromDate: Date | null = null;
  toDate: Date | null = null;

  /**
   * Valid event type values accepted by the API and shown in the filter dropdown.
   * @readonly
   */
  readonly EVENT_TYPES = [
    'login',
    'logout',
    'record_access',
    'record_create',
    'record_update',
    'record_delete',
    'system_action',
  ];

  ngOnInit() {
    this.load();
  }

  /**
   * Fetches audit logs from the service, merging any active UI filter values
   * into the base filters object before the request. Date fields are converted
   * to ISO strings for the API.
   * @returns {void}
   */
  load(): void {
    this.loading = true;
    this.error = '';
    const filters: AuditLogFilters = { ...this.filters };
    if (this.searchEventType) filters.eventType = this.searchEventType;
    if (this.filterSuccess !== '') filters.success = this.filterSuccess === 'true';
    if (this.fromDate) filters.from = this.fromDate.toISOString();
    if (this.toDate) filters.to = this.toDate.toISOString();
    this.auditLogService.getLogs(filters).subscribe({
      next: (res) => {
        this.logs = res.logs;
        this.total = res.total;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.error = 'Failed to load audit logs.';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Resets to page 1 and reloads with current filter values.
   * @returns {void}
   */
  applyFilters(): void {
    this.filters.offset = 0;
    this.load();
  }

  /**
   * Clears all active filters and reloads the default log view.
   * @returns {void}
   */
  clearFilters(): void {
    this.searchEventType = '';
    this.filterSuccess = '';
    this.fromDate = null;
    this.toDate = null;
    this.filters = { limit: 50, offset: 0 };
    this.load();
  }

  /**
   * Advances to the next page by incrementing the offset and reloading.
   * @returns {void}
   */
  nextPage(): void {
    this.filters.offset = (this.filters.offset ?? 0) + (this.filters.limit ?? 50);
    this.load();
  }

  /**
   * Returns to the previous page, flooring offset at 0.
   * @returns {void}
   */
  prevPage(): void {
    this.filters.offset = Math.max(0, (this.filters.offset ?? 0) - (this.filters.limit ?? 50));
    this.load();
  }

  /**
   * Current 1-based page number derived from offset and limit.
   * @returns {number}
   */
  get currentPage(): number {
    return Math.floor((this.filters.offset ?? 0) / (this.filters.limit ?? 50)) + 1;
  }

  /**
   * Total number of pages derived from total log count and limit.
   * @returns {number}
   */
  get totalPages(): number {
    return Math.ceil(this.total / (this.filters.limit ?? 50));
  }

  /**
   * Returns a human-readable success/failure label for a log entry.
   * @param {AuditLog} log - The audit log entry to evaluate.
   * @returns {string} 'Success' or 'Failed'.
   */
  successLabel(log: AuditLog): string {
    return log.success ? 'Success' : 'Failed';
  }

  /**
   * Returns a display name for who performed the action.
   * Falls back to a user ID string if name data is unavailable, or 'System' if no user at all.
   * @param {AuditLog} log - The audit log entry to evaluate.
   * @returns {string} Full name, 'User #id', or 'System'.
   */
  performedBy(log: AuditLog): string {
    if (log.user) {
      const fullName = `${log.user.first_name} ${log.user.last_name}`;
      const preferred = log.user.preferredName;
      return preferred && preferred !== log.user.first_name
        ? `${fullName} (${preferred})`
        : fullName;
    }
    if (log.userId) return `User #${log.userId}`;
    return 'System';
  }

  /**
   * Formats an ISO timestamp string into a short, readable locale string.
   * @param {string} ts - ISO 8601 timestamp string.
   * @returns {string} Formatted date/time, e.g. 'May 18, 2026, 02:30 PM'.
   */
  formatTimestamp(ts: string): string {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
